import pb from '../lib/pocketbase';
import { AuthUser } from '../types';
import { loginAttemptTracker } from './loginAttemptTracker';
import { loggingService } from './loggingService';
import { debugLog } from './debugConfig';

export class PocketBaseAuthService {
  // Sign up new user
  static async signUp(
    email: string,
    password: string,
    metadata: { name: string; role: string; partner_id?: string; [key: string]: any }
  ): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Creating new user...');
      await debugLog('PocketBase: User data:', { email, password: '***', metadata });
      
      // Normalize email to lowercase for consistency
      const normalizedEmail = email.toLowerCase().trim();

      // PocketBase auth collections allow additional custom fields.
      // Ensure we persist any extra metadata (activation_method, tokens, status, etc.).
      const extra: any = { ...metadata };
      delete extra.email;
      delete extra.password;
      delete extra.passwordConfirm;
      
      const data = {
        email: normalizedEmail,
        password,
        passwordConfirm: password,
        emailVisibility: true,
        ...extra,
      };

      await debugLog('PocketBase: Sending data:', { ...data, password: '***', passwordConfirm: '***' });
      const record = await pb.collection('users').create(data);
      
      await debugLog('PocketBase: User created successfully');
      
      return {
        user: {
          id: record.id,
          email: record.email,
          name: record.name,
          role: record.role,
          created_at: record.created,
          partner_id: record.partner_id || null
        } as AuthUser,
        error: null
      };
    } catch (error: any) {
      console.error('PocketBase: Sign up error:', error);
      console.error('PocketBase: Error details:', error.data);
      return {
        user: null,
        error: error.data ? JSON.stringify(error.data) : (error.message || 'Failed to create user')
      };
    }
  }

  // Sign in user
  static async signIn(email: string, password: string): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Signing in...');
      
      // Normalize email to lowercase for case-insensitive login
      const normalizedEmail = email.toLowerCase().trim();

      pb.authStore.clear();
      
      // Check if account is locked due to too many failed attempts
      if (loginAttemptTracker.isLocked(normalizedEmail)) {
        const remainingSeconds = loginAttemptTracker.getRemainingLockoutTime(normalizedEmail);
        const remainingMinutes = Math.ceil(remainingSeconds / 60);
        console.warn(`🔒 Login blocked for ${normalizedEmail} - account locked for ${remainingMinutes}m`);
        
        // Try to get user info for logging (if account exists)
        let userId: string | undefined;
        let userName: string | undefined;
        let partnerId: string | undefined;
        try {
          const users = await pb.collection('users').getFullList({
            filter: `email = "${normalizedEmail}"`,
            limit: 1
          });
          if (users.length > 0) {
            userId = users[0].id;
            userName = users[0].name;
            partnerId = users[0].partner_id;
          }
        } catch (e) {
          // Ignore - user might not exist
        }
        
        // Log blocked login attempt
        await loggingService.logBlockedLoginAttempt(
          normalizedEmail,
          remainingMinutes,
          userId,
          userName,
          partnerId
        );
        
        // Format time display
        let timeDisplay = '';
        if (remainingMinutes >= 60) {
          const hours = Math.floor(remainingMinutes / 60);
          const mins = remainingMinutes % 60;
          timeDisplay = mins > 0 ? `${hours} hour${hours > 1 ? 's' : ''} and ${mins} minute${mins > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''}`;
        } else {
          timeDisplay = `${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
        }
        
        return {
          user: null,
          error: `Too many failed login attempts. Your account has been temporarily locked. Please try again in ${timeDisplay}.`
        };
      }
      
      const authData = await pb.collection('users').authWithPassword(normalizedEmail, password);
      
      await debugLog('PocketBase: Sign in successful');
      
      // Reset failed login attempts on successful login
      loginAttemptTracker.recordSuccessfulLogin(normalizedEmail);
      
      // Update last_login timestamp
      try {
        await pb.collection('users').update(authData.record.id, {
          last_login: new Date().toISOString()
        });
        await debugLog('PocketBase: Updated last_login timestamp');
      } catch (updateError) {
        console.warn('PocketBase: Failed to update last_login:', updateError);
        // Don't fail the login if we can't update last_login
      }
      
      return {
        user: {
          id: authData.record.id,
          email: authData.record.email,
          name: authData.record.name,
          role: authData.record.role,
          created_at: authData.record.created,
          partner_id: authData.record.partner_id || null,
          first_login_completed: authData.record.first_login_completed,
          last_login: new Date().toISOString() // Set current time as last login
        } as AuthUser,
        error: null
      };
    } catch (error: any) {
      console.error('PocketBase: Sign in error:', error);
      console.error('PocketBase: Sign in error data:', error?.data || error?.response?.data);
      
      // Normalize email for tracking
      const normalizedEmail = email.toLowerCase().trim();
      
      // Try to get user info for logging (if account exists)
      let userId: string | undefined;
      let userName: string | undefined;
      let partnerId: string | undefined;
      try {
        const users = await pb.collection('users').getFullList({
          filter: `email = "${normalizedEmail}"`,
          limit: 1
        });
        if (users.length > 0) {
          userId = users[0].id;
          userName = users[0].name;
          partnerId = users[0].partner_id;
        }
      } catch (e) {
        // Ignore - user might not exist
      }
      
      // Get attempt count BEFORE recording
      const attemptCountBefore = loginAttemptTracker.getAttemptCount(normalizedEmail);
      
      // Record failed login attempt
      loginAttemptTracker.recordFailedAttempt(normalizedEmail);
      
      // Get remaining attempts AFTER recording this failure
      const remainingAttempts = loginAttemptTracker.getRemainingAttempts(normalizedEmail);
      const attemptNumber = attemptCountBefore + 1;
      
      // Log failed login attempt
      await loggingService.logFailedLogin(
        normalizedEmail,
        attemptNumber,
        remainingAttempts,
        userId,
        userName,
        partnerId
      );
      
      // If this was the final attempt that triggered lockout, log it
      if (remainingAttempts === 0) {
        await loggingService.logAccountLockout(
          normalizedEmail,
          15, // 15 minutes lockout
          userId,
          userName,
          partnerId
        );
      }
      
      let errorMessage =
        error?.data?.message ||
        error?.response?.data?.message ||
        'Invalid email or password';
      
      // Add warning if getting close to lockout (check the count AFTER recording)
      if (remainingAttempts <= 2 && remainingAttempts > 0) {
        errorMessage += `. ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining before 15-minute lockout.`;
      } else if (remainingAttempts === 0) {
        errorMessage = 'Too many failed login attempts. Your account has been temporarily locked for 15 minutes.';
      }
      
      return {
        user: null,
        error: errorMessage
      };
    }
  }

  // Sign out user
  static async signOut(): Promise<{ error: string | null }> {
    try {
      pb.authStore.clear();
      await debugLog('PocketBase: Signed out successfully');
      return { error: null };
    } catch (error: any) {
      return { error: error.message || 'Sign out failed' };
    }
  }

  // Get current user
  static async getCurrentUser(): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      await debugLog('PocketBase: Getting current user...');
      
      if (!pb.authStore.isValid) {
        await debugLog('PocketBase: No valid auth token');
        return { user: null, error: 'No active session' };
      }

      // Refresh the auth token if needed
      try {
        await pb.collection('users').authRefresh();
      } catch (refreshError) {
        console.warn('PocketBase: Token refresh failed, clearing auth');
        pb.authStore.clear();
        return { user: null, error: 'Session expired' };
      }

      const record = pb.authStore.model;
      
      if (!record) {
        return { user: null, error: 'No user found' };
      }

      await debugLog('PocketBase: Current user found');
      
      return {
        user: {
          id: record.id,
          email: record.email,
          name: record.name,
          role: record.role,
          created_at: record.created,
          partner_id: record.partner_id || null,
          first_login_completed: record.first_login_completed
        } as AuthUser,
        error: null
      };
    } catch (error: any) {
      console.error('PocketBase: Get current user error:', error);
      return {
        user: null,
        error: error.message || 'Failed to get current user'
      };
    }
  }

  // Update user profile
  static async updateUser(id: string, updates: Partial<AuthUser>): Promise<{ user: AuthUser | null; error: string | null }> {
    try {
      const record = await pb.collection('users').update(id, updates);
      
      return {
        user: {
          id: record.id,
          email: record.email,
          name: record.name,
          role: record.role,
          created_at: record.created,
          partner_id: record.partner_id || null
        } as AuthUser,
        error: null
      };
    } catch (error: any) {
      return {
        user: null,
        error: error.message || 'Failed to update user'
      };
    }
  }

  // Change password
  static async changePassword(oldPassword: string, newPassword: string): Promise<{ error: string | null }> {
    try {
      const userId = pb.authStore.model?.id;
      if (!userId) {
        return { error: 'No user logged in' };
      }

      await pb.collection('users').update(userId, {
        oldPassword: oldPassword,
        password: newPassword,
        passwordConfirm: newPassword
      });

      return { error: null };
    } catch (error: any) {
      console.error('User update error:', error);
      
      // Extract more detailed error message
      if (error.response?.data?.data?.oldPassword) {
        return { error: 'Current password is incorrect' };
      }
      
      return { error: error.message || 'Failed to change password' };
    }
  }

  // Reset password
  static async resetPassword(email: string): Promise<{ error: string | null }> {
    try {
      // Normalize email to lowercase for consistency
      const normalizedEmail = email.toLowerCase().trim();
      
      await pb.collection('users').requestPasswordReset(normalizedEmail);
      return { error: null };
    } catch (error: any) {
      return { error: error.message || 'Failed to send reset email' };
    }
  }

  // Listen to auth state changes
  static onAuthStateChange(callback: (user: AuthUser | null) => void) {
    // PocketBase doesn't have a built-in listener, but we can check on interval
    // or manually trigger after auth operations
    const checkAuth = async () => {
      const { user } = await this.getCurrentUser();
      callback(user);
    };

    // Initial check
    checkAuth();

    // Return a cleanup function
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            // Cleanup if needed
          }
        }
      }
    };
  }
}
